import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { normalizarValorOrdenacao } from '../../../../../shared/helpers/ordenacao-tabela.helper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../../shared/components/empty-list/empty-list.component';
import { FiltroExtracao } from '../../../../../shared/components/extracao-dados-modal/extracao-dados-modal.component';
import { LoadingBoxDefaultListComponent } from '../../../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ControleEstoqueService } from '../../../../../shared/services/apis/produto/controle-estoque.service';
import { MaskService } from '../../../../../shared/services/mask.service';
import { LocalizacaoEstoque } from '../../../../../shared/services/models/produto/localizacao-estoque.model';
import { ControleEstoqueDetalheReservasRequest } from '../../../../../shared/services/requests/produto/controle-estoque-detalhe-reservas.request';
import { ControleEstoqueDetalheReservaStruct } from '../../../../../shared/services/structs/produto/controle-estoque-detalhe-reserva.struct';
import { OrigemReservaEnum } from '@shared/enums/produto/origem-reserva.enum';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';

/** Indicador da coluna Origem: letras, cor (paleta --indicador-cor-*), texto do tooltip e rota do documento. */
interface IndicadorOrigemReserva {
  sigla: string;
  cor: string;
  rotulo: string;
  rota: string | null;
}

enum PeriodoEnum {
  SemFiltro = 0,
  Ultimos30Dias = 1,
  DoMes = 2,
  DoIntervalo = 3,
}

@Component({
  selector: 'app-aba-reservas',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDatepickerModule,
    EmptyListComponent,
    LoadingBoxDefaultListComponent,
    AbntPipe,
  ],
  templateUrl: './aba-reservas.component.html',
  styleUrls: ['./aba-reservas.component.css'],
})
export class AbaReservasComponent implements OnInit, OnChanges {
  @Input() idProduto!: number;
  @Input() listaLocalizacaoEstoque: LocalizacaoEstoque[] = [];
  @Input() ativa: boolean = false;
  /** Permissão "Reserva de estoque – Excluir": sem ela a exclusão por linha fica desabilitada. */
  @Input() podeExcluir: boolean = false;

  @Output() filtroLocalChange = new EventEmitter<number | null>();
  @Output() executarStub = new EventEmitter<string>();
  @Output() totalReservadoChange = new EventEmitter<number>();
  @Output() selecaoChange = new EventEmitter<boolean>();
  @Output() reservaLiberada = new EventEmitter<void>();

  public model!: FormGroup;
  public _isLoading: boolean = false;
  public _dataSource = new MatTableDataSource<ControleEstoqueDetalheReservaStruct>();
  public _paginaAtual: number = 0;
  public _ultimaPagina: boolean = false;
  public _carregouUmaVez: boolean = false;
  private pesquisaSubscription?: Subscription;
  public _totalReservado: number = 0;

  public periodoSelecionado: PeriodoEnum = PeriodoEnum.Ultimos30Dias;
  public PeriodoEnum = PeriodoEnum;
  public periodoIntervaloDe: Date | null = null;
  public periodoIntervaloAte: Date | null = null;

  @ViewChild('periodoMenuTrigger') periodoMenuTrigger?: MatMenuTrigger;
  @ViewChild(MatSort) set sort(sort: MatSort | undefined) {
    if (!sort) return;
    this._dataSource.sort = sort;
  }

  private periodoSelecionadoAplicado: PeriodoEnum = PeriodoEnum.Ultimos30Dias;
  private periodoIntervaloDeAplicado: Date | null = null;
  private periodoIntervaloAteAplicado: Date | null = null;
  private periodoMenuFoiAplicado: boolean = false;

  public selection = new SelectionModel<ControleEstoqueDetalheReservaStruct>(true, []);

  public displayedColumns: string[] = [
    'select', 'dataHora', 'localizacao', 'quantidade', 'origem', 'loteSerie', 'validade', 'observacao', 'actions',
  ];

  constructor(
    private formBuilder: FormBuilder,
    private controleEstoqueService: ControleEstoqueService,
    private alertService: AlertService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
      idLocalizacaoEstoque: [null],
    });

    this.aplicarLocalizacaoPadrao();

    this.setPeriodoUltimos30Dias();
    this.salvarPeriodoAplicado();
    this.configurarOrdenacao();

    this.selection.changed.subscribe(() => {
      this.selecaoChange.emit(this.selection.hasValue());
    });

    if (this.ativa && this.idProduto && !this._carregouUmaVez) {
      this.clickPesquisar();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['listaLocalizacaoEstoque']) {
      this.aplicarLocalizacaoPadrao();
    }

    if (!this.model) return;

    if (changes['ativa'] && this.ativa && !this._carregouUmaVez && this.idProduto) {
      this.clickPesquisar();
    }
  }

  // Filtro de Localização deveria abrir na localização padrão do sistema, não em "todos".
  private aplicarLocalizacaoPadrao(): void {
    if (!this.model || this.model.get('idLocalizacaoEstoque')?.value) return;

    const localPadrao = this.listaLocalizacaoEstoque?.find(l => l.isPadrao);
    if (!localPadrao) return;

    this.model.get('idLocalizacaoEstoque')?.setValue(localPadrao.idLocalizacaoEstoque);

    // A lista de locais chega depois da primeira busca: refaz mesmo com ela ainda em andamento,
    // senão a tela mostra o local padrão no filtro com a lista de todos os locais.
    if (this._carregouUmaVez || this._isLoading) {
      this.clickPesquisar();
    }
  }

  public clickPesquisar(): void {
    // Descarta a busca em andamento: o resultado dela é de filtros que já mudaram.
    this.pesquisaSubscription?.unsubscribe();
    this._isLoading = false;
    this._paginaAtual = 0;
    this._dataSource.data = [];
    this.selection.clear();
    this._ultimaPagina = false;
    this.pesquisar();
  }

  public getRequestAtual(): ControleEstoqueDetalheReservasRequest {
    const request = new ControleEstoqueDetalheReservasRequest();
    const datas = this.getDatasPeriodo();

    request.searchText = this.model.get('pesquisa')?.value || undefined;
    request.idLocalizacaoEstoque = this.model.get('idLocalizacaoEstoque')?.value ?? null;
    request.dataInicial = datas.inicio ? this.formatarData(datas.inicio) : null;
    request.dataFinal = datas.fim ? this.formatarData(datas.fim) : null;

    return request;
  }

  public getFiltrosExtracao(): FiltroExtracao[] {
    const request = this.getRequestAtual();
    const filtros: FiltroExtracao[] = [
      {
        label: 'Período',
        formControlName: 'periodoReservas',
        valor: this.periodoSelecionado,
        valorExibicao: this.periodoLabel,
      },
    ];

    if (request.searchText) {
      filtros.push({
        label: 'Pesquisa',
        formControlName: 'searchText',
        valor: request.searchText,
      });
    }

    if (request.idLocalizacaoEstoque) {
      filtros.push({
        label: 'Localização',
        formControlName: 'idLocalizacaoEstoque',
        valor: request.idLocalizacaoEstoque,
        valorExibicao: this.getDescricaoLocalizacao(request.idLocalizacaoEstoque),
      });
    }

    if (request.dataInicial) {
      filtros.push({
        label: 'Data inicial',
        formControlName: 'dataInicial',
        valor: request.dataInicial,
        valorExibicao: this.formatarDataExibicao(request.dataInicial),
      });
    }

    if (request.dataFinal) {
      filtros.push({
        label: 'Data final',
        formControlName: 'dataFinal',
        valor: request.dataFinal,
        valorExibicao: this.formatarDataExibicao(request.dataFinal),
      });
    }

    return filtros;
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.pesquisar();
  }

  public limparFiltros(): void {
    this.model.patchValue({
      pesquisa: '',
      idLocalizacaoEstoque: null,
    });
    this.setPeriodoUltimos30Dias();
    this.salvarPeriodoAplicado();
    this.filtroLocalChange.emit(null);
    this.clickPesquisar();
  }

  public aplicarFiltros(): void {
    const idLocal = this.model.get('idLocalizacaoEstoque')?.value;
    this.filtroLocalChange.emit(idLocal ?? null);
    this.clickPesquisar();
  }

  public get qtdFiltrosAvancados(): number {
    if (!this.model) return 0;
    return this.model.get('idLocalizacaoEstoque')?.value != null ? 1 : 0;
  }

  private cloneDate(data: Date | null): Date | null {
    return data ? new Date(data) : null;
  }

  private salvarPeriodoAplicado(): void {
    this.periodoSelecionadoAplicado = this.periodoSelecionado;
    this.periodoIntervaloDeAplicado = this.cloneDate(this.periodoIntervaloDe);
    this.periodoIntervaloAteAplicado = this.cloneDate(this.periodoIntervaloAte);
  }

  private restaurarPeriodoAplicado(): void {
    this.periodoSelecionado = this.periodoSelecionadoAplicado;
    this.periodoIntervaloDe = this.cloneDate(this.periodoIntervaloDeAplicado);
    this.periodoIntervaloAte = this.cloneDate(this.periodoIntervaloAteAplicado);
  }

  public prepararPeriodoIntervalo(): void {
    this.periodoMenuFoiAplicado = false;

    if (
      this.periodoSelecionado === PeriodoEnum.DoIntervalo &&
      (!this.periodoIntervaloDe || !this.periodoIntervaloAte)
    ) {
      this.setPeriodoUltimos7DiasIntervalo();
    }
  }

  public onPeriodoMenuClosed(): void {
    if (!this.periodoMenuFoiAplicado) {
      this.restaurarPeriodoAplicado();
    }
  }

  public cancelarPeriodoIntervalo(trigger?: MatMenuTrigger): void {
    this.restaurarPeriodoAplicado();
    this.periodoMenuFoiAplicado = true;
    const menuTrigger = trigger ?? this.periodoMenuTrigger;
    setTimeout(() => menuTrigger?.closeMenu());
  }

  public aplicarPeriodo(trigger?: MatMenuTrigger): void {
    if (this.periodoSelecionado === PeriodoEnum.DoIntervalo) {
      if (!this.periodoIntervaloDe || !this.periodoIntervaloAte) {
        this.alertService.show(
          'Atenção',
          'Informe as duas datas do intervalo para aplicar o filtro.',
          AlertType.warning
        );
        return;
      }

      if (this.periodoIntervaloAte < this.periodoIntervaloDe) {
        this.alertService.show(
          'Atenção',
          'A data final não pode ser menor que a data inicial.',
          AlertType.warning
        );
        return;
      }
    }

    this.periodoMenuFoiAplicado = true;
    this.salvarPeriodoAplicado();
    this.clickPesquisar();
    const menuTrigger = trigger ?? this.periodoMenuTrigger;
    setTimeout(() => menuTrigger?.closeMenu());
  }

  public selecionarPeriodo(periodo: PeriodoEnum): void {
    this.periodoSelecionado = periodo;

    if (periodo === PeriodoEnum.DoIntervalo) {
      this.setPeriodoUltimos7DiasIntervalo();
    }
  }

  public onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }

  public onDateKeydown(event: KeyboardEvent): void {
    const teclasPermitidas = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
      'Enter',
    ];

    const isAtalho =
      (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());

    if (teclasPermitidas.includes(event.key) || isAtalho) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  public onDateBlur(event: FocusEvent, campo: 'de' | 'ate'): void {
    const input = event.target as HTMLInputElement;
    const valor = input?.value?.trim() || '';

    if (!valor) {
      this.atualizarCampoIntervalo(campo, null);
      return;
    }

    const apenasNumeros = valor.replace(/\D/g, '').substring(0, 8);

    if (apenasNumeros.length !== 8) {
      input.value = '';
      this.atualizarCampoIntervalo(campo, null);
      return;
    }

    const formatado = `${apenasNumeros.substring(0, 2)}/${apenasNumeros.substring(2, 4)}/${apenasNumeros.substring(4, 8)}`;
    input.value = formatado;

    const data = this.parseDataBr(formatado);
    if (!data) {
      this.atualizarCampoIntervalo(campo, null);
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
      return;
    }

    this.onPeriodoIntervaloChange(campo, data);
  }

  public onPeriodoIntervaloChange(campo: 'de' | 'ate', valor: Date | null): void {
    if (!valor) {
      this.atualizarCampoIntervalo(campo, null);
      return;
    }

    const dataNormalizada = new Date(valor);
    dataNormalizada.setHours(0, 0, 0, 0);
    this.atualizarCampoIntervalo(campo, dataNormalizada);

    if (
      this.periodoIntervaloDe &&
      this.periodoIntervaloAte &&
      this.periodoIntervaloAte < this.periodoIntervaloDe
    ) {
      if (campo === 'de') {
        this.periodoIntervaloAte = null;
        this.alertService.show(
          'Atenção',
          'A data final não pode ser menor que a data inicial.',
          AlertType.warning
        );
      } else {
        this.periodoIntervaloDe = null;
        this.alertService.show(
          'Atenção',
          'A data inicial não pode ser maior que a data final.',
          AlertType.warning
        );
      }
    }
  }

  public get periodoLabel(): string {
    switch (this.periodoSelecionado) {
      case PeriodoEnum.SemFiltro:
        return 'sem filtro';
      case PeriodoEnum.Ultimos30Dias:
        return 'últimos 30 dias';
      case PeriodoEnum.DoMes:
        return 'do mês';
      case PeriodoEnum.DoIntervalo:
        return 'do intervalo';
      default:
        return 'últimos 30 dias';
    }
  }

  private setPeriodoUltimos30Dias(): void {
    this.periodoSelecionado = PeriodoEnum.Ultimos30Dias;
    this.periodoIntervaloDe = null;
    this.periodoIntervaloAte = null;
  }

  private setPeriodoUltimos7DiasIntervalo(): void {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    const seteDiasAtras = new Date(hoje);
    seteDiasAtras.setDate(hoje.getDate() - 6);

    this.periodoIntervaloDe = seteDiasAtras;
    this.periodoIntervaloAte = hoje;
  }

  private atualizarCampoIntervalo(campo: 'de' | 'ate', data: Date | null): void {
    if (campo === 'de') {
      this.periodoIntervaloDe = data;
      return;
    }

    this.periodoIntervaloAte = data;
  }

  private parseDataBr(valor: string): Date | null {
    const partes = valor.split('/');
    if (partes.length !== 3) return null;

    const dia = Number(partes[0]);
    const mes = Number(partes[1]);
    const ano = Number(partes[2]);

    if (!Number.isInteger(dia) || !Number.isInteger(mes) || !Number.isInteger(ano)) return null;
    if (ano < 1000 || ano > 9999 || mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;

    const data = new Date(ano, mes - 1, dia);
    data.setHours(0, 0, 0, 0);

    if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) return null;

    return data;
  }

  private getDatasPeriodo(): { inicio: Date | null; fim: Date | null } {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    switch (this.periodoSelecionado) {
      case PeriodoEnum.SemFiltro:
        return { inicio: null, fim: null };
      case PeriodoEnum.Ultimos30Dias: {
        const trintaDiasAtras = new Date(hoje);
        trintaDiasAtras.setDate(hoje.getDate() - 30);
        return { inicio: trintaDiasAtras, fim: hoje };
      }
      case PeriodoEnum.DoMes: {
        const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        const fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
        return { inicio: inicioMes, fim: fimMes };
      }
      case PeriodoEnum.DoIntervalo:
        return { inicio: this.periodoIntervaloDe, fim: this.periodoIntervaloAte };
      default:
        return { inicio: null, fim: null };
    }
  }

  public isAllSelected(): boolean {
    return (
      this._dataSource.data.length > 0 && this.selection.selected.length === this._dataSource.data.length
    );
  }

  public toggleAll(): void {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this._dataSource.data.forEach(r => this.selection.select(r));
    }
  }

  public abrirNovaReserva(): void {
    this.executarStub.emit('Nova reserva');
  }

  public excluirSelecionadas(): void {
    if (!this.podeExcluir || !this.selection.hasValue()) return;
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Excluir reservas',
        description: `Deseja realmente excluir ${this.selection.selected.length} reserva(s)? A quantidade retornará imediatamente ao saldo disponível.`,
        buttonConfirm: 'Excluir',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this.liberar(this.selection.selected.map(r => r.idEstoqueReserva));
      }
    });
  }

  // Letras e cores do protótipo. Orçamento e pedido abrem o mesmo documento: a conversão mantém o idVenda.
  public indicadorOrigem(row: ControleEstoqueDetalheReservaStruct): IndicadorOrigemReserva {
    const id = row.idDocumentoOrigem;
    const documento = row.numeroDocumentoOrigem ? ` ${row.numeroDocumentoOrigem}` : '';

    switch (row.idOrigemReserva) {
      case OrigemReservaEnum.PedidoVenda:
        return {
          sigla: 'PV', cor: 'var(--indicador-cor-f)', rotulo: `Pedido de venda${documento}`,
          rota: id ? `/vendas/processo-venda/documento/${id}` : null,
        };
      case OrigemReservaEnum.Orcamento:
        return {
          sigla: 'OR', cor: 'var(--indicador-cor-v)', rotulo: `Orçamento${documento}`,
          rota: id ? `/vendas/processo-venda/documento/${id}` : null,
        };
      case OrigemReservaEnum.OrdemProducao:
        return {
          sigla: 'OP', cor: 'var(--indicador-cor-e)', rotulo: `Ordem de produção${documento}`,
          rota: id ? `/suprimentos/ordem-producao/${id}/visualizar` : null,
        };
      case OrigemReservaEnum.Transferencia:
        return {
          sigla: 'TR', cor: 'var(--indicador-cor-c)', rotulo: `Transferência${documento}`,
          rota: id ? `/suprimentos/transferencia-estoque/editar/${id}` : null,
        };
      case OrigemReservaEnum.ReservaManual:
        return { sigla: 'M', cor: 'var(--fg-subtle)', rotulo: 'Manual', rota: null };
      default:
        // Origem sem letra definida: sigla da API em cinza, sem link.
        return {
          sigla: row.siglaOrigem || '?', cor: 'var(--fg-subtle)',
          rotulo: `${row.descricaoOrigem || 'Origem'}${documento}`, rota: null,
        };
    }
  }

  public abrirDocumentoOrigem(rota: string | null): void {
    if (!rota) return;
    window.open(rota, '_blank');
  }

  public excluirReserva(row: ControleEstoqueDetalheReservaStruct): void {
    if (!this.podeExcluir) return;
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Excluir reserva',
        description:
          'Deseja realmente excluir esta reserva? A quantidade retornará imediatamente ao saldo disponível.',
        buttonConfirm: 'Excluir',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this.liberar([row.idEstoqueReserva]);
      }
    });
  }

  // Libera as reservas na API.Produto (retorna a quantidade ao disponível) e recarrega a lista.
  private liberar(ids: number[]): void {
    if (!ids.length) return;
    this._isLoading = true;
    this.controleEstoqueService.liberarReserva(ids).subscribe({
      next: resp => {
        this._isLoading = false;
        if (resp?.isError) {
          this.alertService.show('Aviso', resp.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', 'Reserva(s) excluída(s) com sucesso!', AlertType.success);
        this.selection.clear();
        this.reservaLiberada.emit();
        this.clickPesquisar();
      },
      error: () => {
        this._isLoading = false;
        this.alertService.show('Erro', 'Não foi possível excluir a reserva.', AlertType.error);
      },
    });
  }

  private pesquisar(): void {
    if (this._isLoading) return;
    this._isLoading = true;

    const request = this.getRequestAtual();

    this.pesquisaSubscription = this.controleEstoqueService.getReservas(this.idProduto, request, this._paginaAtual).subscribe({
      next: response => {
        this._isLoading = false;
        this._carregouUmaVez = true;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        const novos = response.list || [];
        const acumulado = this._paginaAtual === 0 ? novos : [...this._dataSource.data, ...novos];
        this._dataSource.data = acumulado;
        this._ultimaPagina = !novos.length || acumulado.length >= response.fullListSize;
        this._totalReservado = response.totalReservado;
        this.totalReservadoChange.emit(response.totalReservado);
      },
      error: err => {
        this._isLoading = false;
        this._carregouUmaVez = true;
        this.alertService.show('Erro inesperado', err?.message || err, AlertType.error);
      },
    });
  }

  private formatarData(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  private formatarDataExibicao(data: string): string {
    const partes = data.split('-');
    if (partes.length !== 3) return data;
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
  }

  private getDescricaoLocalizacao(idLocalizacao: number): string {
    const local = this.listaLocalizacaoEstoque.find(x => x.idLocalizacaoEstoque === idLocalizacao);
    if (!local) return `${idLocalizacao}`;
    return `${local.codigo} - ${local.nome}`;
  }

  private configurarOrdenacao(): void {
    this._dataSource.sortingDataAccessor = (item, property): string | number => {
      switch (property) {
        case 'dataHora':
          return normalizarValorOrdenacao(new Date(item.dataHora).getTime());
        case 'quantidade':
          return normalizarValorOrdenacao(item.quantidade ?? 0);
        case 'origem':
          return normalizarValorOrdenacao(item.numeroDocumentoOrigem || item.descricaoOrigem || '');
        case 'localizacao':
          return normalizarValorOrdenacao(item.localizacao || '');
        case 'loteSerie':
          return normalizarValorOrdenacao(item.codigoLote || item.codigoSerie || '');
        case 'validade':
          return normalizarValorOrdenacao(item.dataValidadeReserva ? new Date(item.dataValidadeReserva).getTime() : 0);
        case 'observacao':
          return normalizarValorOrdenacao(item.observacao || '');
        default:
          return normalizarValorOrdenacao((item as any)[property] ?? '');
      }
    };
  }
}
